/**
 * compose-core — the PURE plugin-composition fold behind `GenomeBuilderV3`.
 *
 * A composed genome is nothing more than: an identity (`id`/`name`/`version`/
 * `archetype`), plus every `ComposableGenomePlugin`'s namespaced entity map
 * merged in order, plus the frozen `GenomeV3` envelope. That fold is what a
 * one-genome app boot actually needs — it never calls `.entity()`, `.ref()`,
 * `.schema()`, `.validate()` or any of the builder's other ~16 fluent methods.
 *
 * Those methods cannot be tree-shaken: Rollup keeps every method of an
 * INSTANTIATED class, so an app that only composes plugins still pays for the
 * whole fluent surface (plus `REF_RELS`, the validator slot, and the ref-dedup
 * helpers) on its eager entry chunk. Extracting the fold here — reachable
 * through the granular `@ubiclouds/v5-sdk/genome/compose` subpath, so the
 * `./genome` barrel's other passengers are not dragged along either (the
 * #226/#119 barrel trap) — lets a boot path compose with ONLY the fold.
 *
 * This is the #482c `SchemaParser` → `SchemaEnricher` split applied to the
 * builder, and it is a SPLIT, not a fork: `GenomeBuilderV3.use()` /
 * `.buildUnvalidated()` DELEGATE to the very functions exported here, so there
 * is exactly ONE implementation of the composition semantics with two mouths
 * (the #279 `generate()` → `bakeViewSurface` precedent). A compose-identity
 * test pins `composePluginEntities(meta, plugins)` deep-equal to
 * `GenomeBuilderV3.create(meta).use(…plugins).buildUnvalidated()`.
 *
 * PURE: no I/O, no validation of cross-entity refs (that is `./validate.ts`,
 * reached only through the builder's `.build()`), no React, no side effects.
 *
 * @module @ubiclouds/v5-sdk/genome/compose
 */

import type {
  EntityDef,
  EntityKind,
  GenomeArchetype,
  GenomeV3,
  MountDeclaration,
  SurfacesConfig,
} from './types.js';
import { ENTITY_KINDS, GENOME_ARCHETYPES } from './types.js';
import type { ComposableGenomePlugin, GenomeSurfaceConfig } from '../plugins/contract.js';
import { InvalidPluginEntityError, NamespaceViolationError } from '../plugins/contract.js';

/** The genome identity every compose path builds against. */
export interface GenomeIdentity {
  readonly id: string;
  readonly name: string;
  readonly version: string;
  /** Defaults to `'application'` when omitted. */
  readonly archetype?: GenomeArchetype;
}

/**
 * The accumulated composition state — the builder's internal accumulator, and
 * the input to {@link assembleGenome}.
 */
export interface GenomeAccumulator {
  readonly id: string;
  readonly name: string;
  readonly version: string;
  readonly archetype: GenomeArchetype;
  readonly entities: Readonly<Record<string, EntityDef>>;
  readonly surfaces?: SurfacesConfig;
  readonly mounts?: readonly MountDeclaration[];
}

/**
 * Validate a genome identity and resolve its archetype.
 *
 * Shared by `GenomeBuilderV3.create()` and {@link composePluginEntities} so both
 * mouths reject the same inputs with the same messages. `label` names the
 * caller in the thrown message and defaults to the builder's, so existing
 * builder diagnostics are unchanged.
 */
export function resolveGenomeIdentity(
  input: GenomeIdentity,
  label = 'GenomeBuilderV3.create',
): GenomeArchetype {
  if (!input.id || typeof input.id !== 'string' || input.id.trim() === '') {
    throw new Error(`${label}: id must be a non-empty string`);
  }
  if (!input.name || typeof input.name !== 'string' || input.name.trim() === '') {
    throw new Error(`${label}: name must be a non-empty string`);
  }
  if (!input.version || typeof input.version !== 'string' || input.version.trim() === '') {
    throw new Error(`${label}: version must be a non-empty string`);
  }
  const archetype: GenomeArchetype = input.archetype ?? 'application';
  if (!GENOME_ARCHETYPES.includes(archetype)) {
    throw new Error(`${label}: unknown archetype "${archetype}"`);
  }
  return archetype;
}

/**
 * Derive and validate the entity path for a plugin-contributed entity.
 *
 * If the entity's `meta.id` is namespace-qualified (`ns:kind/id`), it is used
 * as-is; otherwise the path is `{pluginId}:{kind}/{id}`. Either way the `kind/id`
 * part is held to the same strictness `.entity()` applies: non-empty id, known
 * kind, kind matching `entity.kind`.
 */
export function pluginEntityPath(pluginId: string, entity: EntityDef): string {
  const metaId = (entity.meta?.['id'] as string) ?? '';
  const path = metaId.includes(':') ? metaId : `${pluginId}:${entity.kind}/${metaId}`;

  const colonIdx = path.indexOf(':');
  const namespace = path.slice(0, colonIdx);
  const local = path.slice(colonIdx + 1);
  if (namespace.length === 0) {
    throw new InvalidPluginEntityError(pluginId, `entity path "${path}" has an empty namespace`);
  }
  const slashIdx = local.indexOf('/');
  if (slashIdx <= 0) {
    throw new InvalidPluginEntityError(pluginId, `entity path "${path}" must be "{ns}:{kind}/{id}"`);
  }
  const kind = local.slice(0, slashIdx);
  const id = local.slice(slashIdx + 1);
  if (id.length === 0) {
    throw new InvalidPluginEntityError(
      pluginId,
      `entity of kind "${entity.kind}" has an empty id — set meta.id on every plugin entity`,
    );
  }
  if (!ENTITY_KINDS.includes(kind as typeof ENTITY_KINDS[number])) {
    throw new InvalidPluginEntityError(pluginId, `unknown entity kind "${kind}" in path "${path}"`);
  }
  if (kind !== entity.kind) {
    throw new InvalidPluginEntityError(
      pluginId,
      `path "${path}" declares kind "${kind}" but the entity's kind field is "${entity.kind}"`,
    );
  }
  return path;
}

/**
 * Resolve a `ComposableGenomePlugin` into a namespaced entity map.
 *
 * Validates that every contributed entity sits under the plugin's own id
 * namespace or one of its declared `contributesTo` namespaces, and
 * optionally creates a backend service entity from the surface config.
 */
export function resolvePluginSurface(
  plugin: ComposableGenomePlugin,
  config?: GenomeSurfaceConfig,
): ReadonlyMap<string, EntityDef> {
  const raw = plugin.entities();
  const entities = new Map<string, EntityDef>();
  const allowedNamespaces = [plugin.id, ...(plugin.contributesTo ?? [])];

  for (const entity of raw) {
    const path = pluginEntityPath(plugin.id, entity);
    if (!allowedNamespaces.some((ns) => path.startsWith(`${ns}:`))) {
      throw new NamespaceViolationError(plugin.id, path, allowedNamespaces);
    }
    if (entities.has(path)) {
      throw new InvalidPluginEntityError(
        plugin.id,
        `contributes the entity path "${path}" twice`,
      );
    }
    entities.set(path, entity);
  }

  if (config?.backend && plugin.backendContract) {
    const servicePath = `${plugin.id}:${plugin.backendContract.entityPath}`;
    entities.set(servicePath, {
      kind: 'service' as EntityKind,
      meta: {
        contract: plugin.backendContract.interfaceName,
        provider: config.backend.provider,
        config: config.backend.config,
        availableProviders: plugin.backendContract.providers,
      },
    });
  }

  return entities;
}

/**
 * Merge one plugin's resolved surface into an entity map, returning a NEW map.
 * A full-path collision throws — the same plugin composed twice, or two plugins
 * claiming one entity, would otherwise overwrite silently.
 *
 * `label` names the caller in the collision message and defaults to the
 * builder's, so existing `.use()` diagnostics are unchanged.
 */
export function mergePluginSurface(
  entities: Readonly<Record<string, EntityDef>>,
  plugin: ComposableGenomePlugin,
  config?: GenomeSurfaceConfig,
  label = 'GenomeBuilderV3.use',
): Record<string, EntityDef> {
  const surface = resolvePluginSurface(plugin, config);
  const next: Record<string, EntityDef> = { ...entities };
  for (const [path, def] of surface.entries()) {
    if (path in next) {
      throw new Error(
        `${label}("${plugin.id}"): entity path "${path}" already exists in the genome — ` +
        `full-path collisions overwrite silently and are not allowed`,
      );
    }
    next[path] = def;
  }
  return next;
}

/** Freeze an accumulator into the finished `GenomeV3` envelope. */
export function assembleGenome(acc: GenomeAccumulator): GenomeV3 {
  const genome: GenomeV3 = {
    v: 3,
    archetype: acc.archetype,
    id: acc.id,
    name: acc.name,
    version: acc.version,
    entities: acc.entities,
    ...(acc.surfaces && { surfaces: acc.surfaces }),
    ...(acc.mounts && acc.mounts.length > 0 && { mounts: acc.mounts }),
  };
  return Object.freeze(genome);
}

/**
 * Compose a `GenomeV3` from an identity plus an ordered plugin list — the pure
 * fold `GenomeBuilderV3.create(meta).use(…plugins).buildUnvalidated()` performs,
 * without instantiating the builder.
 *
 * NO cross-ref validation (that is the builder's `.build()` → `./validate.ts`);
 * this is the composition-only mouth an app boot uses.
 */
export function composePluginEntities(
  meta: GenomeIdentity,
  plugins: readonly ComposableGenomePlugin[],
): GenomeV3 {
  const archetype = resolveGenomeIdentity(meta);
  let entities: Readonly<Record<string, EntityDef>> = Object.freeze({});
  for (const plugin of plugins) {
    entities = Object.freeze(mergePluginSurface(entities, plugin));
  }
  return assembleGenome({
    id: meta.id,
    name: meta.name,
    version: meta.version,
    archetype,
    entities,
  });
}
